import type { ArticleCover, ManifestArticle } from './articleTypes'

/**
 * VRG-674 / VRG-685 — the pure half of the Articles page. Pinned by
 * `apps/platform/test/articles-manifest.guard.spec.ts`.
 */

/** What the page says about an article, combining the WEBSITE's status and vera-2's approval. */
export type ArticleState = 'published' | 'awaiting_publish' | 'changed' | 'needs_review'

export interface ApprovalLike {
  section: string
  slug: string
  contentHash: string
}

export interface PublicationLike {
  isPublished: boolean
  revalidate: { status: 'pending' | 'sent' | 'failed' | 'not_configured' }
}

/**
 * Published wins. Since VRG-685 vera-2 OWNS publication (the website reads the published set
 * from it), so `publication.isPublished` is the source of truth; the manifest's `status` is kept
 * as a second input only for an article the website still lists by hand in PUBLIC_ROUTES. Either
 * way, if the website serves it, it is live whatever approval says. Otherwise an approval counts
 * only for the version it was given to — a later edit changes the hash and the article reads
 * "Changed since approval", never "Awaiting publish" with words nobody approved.
 */
export const articleState = (
  a: Pick<ManifestArticle, 'status' | 'contentHash'>,
  approval: Pick<ApprovalLike, 'contentHash'> | undefined,
  publication?: Pick<PublicationLike, 'isPublished'>,
): ArticleState => {
  if (publication?.isPublished || a.status === 'published') return 'published'
  if (!approval) return 'needs_review'
  return approval.contentHash === a.contentHash ? 'awaiting_publish' : 'changed'
}

/**
 * VRG-685 — the website was not confirmed told about the latest change: the signed revalidate
 * webhook failed, timed out, or is not configured here. The change itself stands, and the
 * website's own ~60s refresh still picks it up; this only says "not confirmed yet".
 */
export const isWebsiteRefreshPending = (p: Pick<PublicationLike, 'revalidate'> | undefined) =>
  !!p && p.revalidate.status !== 'sent'

/** Confirm copy (§43: name the consequence). Pinned by the guard spec. */
export const publishTitle = (title: string) => `Publish "${title}"`
export const unpublishTitle = (title: string) => `Unpublish "${title}"`
export const publishConsequence = (section: 'insights' | 'learn') =>
  `This goes live on vera.inc within a minute: listed on ${section === 'learn' ? '/learn' : '/insights'} and added to the sitemap and llms.txt so Google, Bing and AI answer engines can find it. Bing is notified automatically, and you'll get a reminder to request indexing in Google.`
export const UNPUBLISH_CONSEQUENCE =
  'The page will return "not found" and leave the sitemap. Google may still show it until it re-checks the page, and shared links will break.'
export const PUBLISH_PERMISSION_TOOLTIP = "You need the 'Publish articles' permission. Ask an admin to grant it."

export const approvalKey = (a: { section: string; slug: string }) => `${a.section}/${a.slug}`

/** VRG-733 — explainers have no hero image by decision, so "none" on an explainer says so. */
export const coverLabel = (c: ArticleCover, section?: string): string =>
  c.type === 'image'
    ? 'Generated image'
    : c.type === 'motif'
      ? 'Drawn motif'
      : section === 'learn'
        ? 'No cover (explainer)'
        : 'None'

export const SITE_URL = 'https://vera.inc'

/**
 * The link a row offers. A published article links to its live page. A staged one links to
 * the same address, which on production renders only for someone holding the website's staging
 * unlock (anyone else gets the site's 404) — so it is labelled "Preview", not "Live".
 */
export const articleLink = (a: Pick<ManifestArticle, 'path' | 'status'> & { href?: string }) => ({
  // VRG-733 — `href` is where the website serves it now (explainers: /insights/explainers/<slug>).
  href: `${SITE_URL}${a.href ?? a.path}`,
  label: a.status === 'published' ? 'Live' : 'Preview',
})

/** Per-page performance, keyed by path, from the SEO "pages to leads" rows vera-2 already has. */
export interface PagePerformance {
  clicks: number | null
  impressions: number | null
  visitors: number | null
  signups: number | null
}

export const performanceByPath = <
  R extends { path: string; clicks: number | null; impressions: number | null; visitors: number | null; signups: number | null },
>(
  rows: readonly R[] | undefined,
): Map<string, PagePerformance> => {
  const map = new Map<string, PagePerformance>()
  for (const r of rows ?? []) {
    // Normalise a trailing slash so `/insights/x/` and `/insights/x` are one page.
    const path = r.path.length > 1 && r.path.endsWith('/') ? r.path.slice(0, -1) : r.path
    map.set(path, { clicks: r.clicks, impressions: r.impressions, visitors: r.visitors, signups: r.signups })
  }
  return map
}

/*
  THE REVIEW STEPS (Spencer, 2026-10-29: "I want to see in internal the process I would normally
  go through to approve the articles and images"). One article, in order:

    Words · Cover · Approve · Narration · Preview · Publish

  The ORDER IS LOAD-BEARING, not a suggestion. An approval carries the manifest's `contentHash`,
  and for a perspective that hash covers what a reader sees, cover INCLUDED
  (vera-website scripts/articles-manifest.mjs: title, excerpt, body, sources, image, cover). So a
  cover locked after the approval changes the hash and the article reads "Changed since approval".
  Cover first, then one approval for words and cover together. Narration's own hash is the TEXT
  only (scripts/generate-article-audio.mjs), so a later cover change never makes audio stale, and
  narration waits for the approval (copy confirmed first; every ElevenLabs run needs Spencer's yes).

  Reading the words is not recorded anywhere (no editor, no "read" event), so Words has no done
  state of its own: the approval is what records it. Preview is likewise unrecorded; it is done
  only in the sense that the page is live. Neither is faked.
*/
export type ReviewStepKey = 'words' | 'cover' | 'approve' | 'narration' | 'preview' | 'publish'
export type ReviewStepState = 'done' | 'todo' | 'blocked' | 'skipped' | 'later'

export interface ReviewStep {
  key: ReviewStepKey
  label: string
  state: ReviewStepState
  note: string
}

export interface ReviewInput {
  section: 'insights' | 'learn'
  state: ArticleState
  /** A cover approved (locked) in the VRG-733 tool for this article, in the tool's mode. */
  coverLocked: boolean
  /** The website already shows a generated image (an earlier cover, made before the tool). */
  websiteHasImage: boolean
}

export const reviewSteps = ({ section, state, coverLocked, websiteHasImage }: ReviewInput): ReviewStep[] => {
  const approved = state === 'awaiting_publish' || state === 'published'
  const needsCover = section === 'insights'
  const coverTodo = needsCover && !coverLocked
  const coverNote = !needsCover
    ? 'Explainers use a drawn pattern, not a cover.'
    : coverLocked
      ? 'Locked in the cover tool.'
      : websiteHasImage
        ? 'The site shows an earlier image; no cover is locked in the tool yet.'
        : 'No cover yet.'
  return [
    {
      key: 'words',
      label: 'Words',
      state: approved ? 'done' : 'todo',
      note: state === 'changed' ? 'Changed since you approved it. Read it again.' : approved ? 'Approved with the cover.' : 'Read them on Overview.',
    },
    { key: 'cover', label: 'Cover', state: !needsCover ? 'skipped' : coverLocked ? 'done' : 'todo', note: coverNote },
    {
      key: 'approve',
      label: 'Approve',
      state: approved ? 'done' : coverTodo ? 'blocked' : 'todo',
      note: approved
        ? 'Words and cover, as they stand.'
        : coverTodo
          ? 'Lock the cover first: the approval covers the cover too.'
          : state === 'changed'
            ? 'Approve this version.'
            : 'One approval for words and cover.',
    },
    {
      key: 'narration',
      label: 'Narration',
      state: 'later',
      note: approved
        ? 'Arrives with the narration work (agent/article-audio). Each run needs your yes.'
        : 'After the approval. Arrives with the narration work (agent/article-audio).',
    },
    {
      key: 'preview',
      label: 'Preview',
      state: state === 'published' ? 'done' : 'todo',
      note: state === 'published' ? 'Live on vera.inc.' : 'The staged page, with the staging unlock.',
    },
    {
      key: 'publish',
      label: 'Publish',
      state: state === 'published' ? 'done' : approved ? 'todo' : 'blocked',
      note: state === 'published' ? 'Live.' : approved ? 'Only when you say publish.' : 'Approve first.',
    },
  ]
}

/** The list's "Next step": the one thing that moves the piece forward, or null once it is live. */
export const nextReviewStep = (input: ReviewInput): string | null => {
  if (input.state === 'published') return null
  if (input.section === 'insights' && !input.coverLocked) return 'Lock a cover'
  if (input.state === 'changed') return 'Re-read and approve'
  if (input.state === 'needs_review') return 'Read and approve'
  return 'Preview, then publish'
}

/** The step the page outlines: the first open one that moves the piece (reading and previewing are never recorded). */
export const currentReviewKey = (steps: ReviewStep[]): ReviewStepKey | undefined =>
  steps.find((s) => s.state === 'todo' && s.key !== 'words' && s.key !== 'preview')?.key
